<template>
  <Container v-if="chartData && regionData">
    <tooltip-provider
      :eventBus="tooltipEventBus"
      :chartMetadata="{ axis: chartData.axis, series: chartData.series }"
      :tooltipContentComponent="pathTooltipContentComponent"
      :tooltipContainerComponent="pathTooltipContainerComponent"
    >
      <chart-svg :viewBox="`0 0 ${size.height} ${size.width}`" preserveAspectRatio="xMidYMin meet">
        <g>
          <component
            v-if="mapPathComponent"
            :is="mapPathComponent"
            :feature-collection="featureCollection"
            :chart-data="chartData"
            :find-in-data="findInData"
            :path="path"
            :fill="fill"
            :stroke="stroke"
            :stroke-width="strokeWidth"
            @regionClick="regionClick"
            @enter="showTooltip"
            @leave="hideTooltip"
          />

          <component
            v-if="mapLabelComponent && labelsVisibility"
            :is="mapLabelComponent"
            :feature-collection="featureCollection"
            :chart-data="chartData"
            :find-in-data="findInData"
            :path="path"
            :label-font-size="labelFontSize"
          />
        </g>
      </chart-svg>
    </tooltip-provider>
    {{ renderHover() }}
  </Container>
</template>

<script>
import { always, find, identity, pathOr, prop } from 'ramda';
import { geoPath, geoIdentity, geoMercator } from 'd3-geo';
import { feature } from 'topojson';

import { ParentSizeMixin, ParentSize } from '../../responsive';
import { ChartsMixin } from '../charts.mixin';
import { ChartSvg } from '../base';
import { Container } from '../charts.styles';
import { DEFAULT_SIZE, REGIONS_DATA, REGIONS_SIZE } from './regionalMapChart.constants';
import MapPathComponent from './mapPath.component';
import MapLabelComponent from './mapLabel.component';
import { TooltipProviderMixin } from '../tooltip';
import PathTooltipContent from './pathTooltip/pathTooltipContent.component';
import { PACIFIC } from '@shared/constants';

export default {
  mixins: [ChartsMixin, ParentSizeMixin, TooltipProviderMixin],
  components: { ChartSvg, Container, ParentSize },
  props: {
    region: { type: String, default: '' },
    customTopoJson: { type: Object, default: null },
    fill: { type: Function, default: always('#fff') },
    stroke: { type: Function, default: always('#000') },
    strokeWidth: { type: Function, default: always(1) },
    margin: { type: Number, default: 0 },
    labelFontSize: { type: Function, default: always('10') },
    mapPathComponent: { type: Object, default: () => MapPathComponent },
    labelsVisibility: { type: Boolean, default: false },
    mapLabelComponent: { type: Object, default: () => MapLabelComponent },
    pathTooltipContainerComponent: { type: Object },
    pathTooltipContentComponent: { type: Object, default: () => PathTooltipContent },
    regionClick: { type: Function, default: identity },
  },
  computed: {
    size() {
      return REGIONS_SIZE[this.region] || DEFAULT_SIZE;
    },
    path() {
      const { margin } = this;

      const projection =
        this.region === PACIFIC
        ? geoMercator()
          .translate([this.size.width /2, this.size.height / 2])
          .scale(575)
          .center([-95, -6.314993])
          .rotate([90, 0])
          // ? geoIdentity().reflectY(true).scale(18).translate([-2350, 430])
          : geoIdentity().fitExtent(
              [
                [margin, margin],
                [this.size.height - margin, this.size.width - margin],
              ],
              this.featureCollection
            );

      return geoPath().projection(projection);
    },
    regionData() {
      return this.customTopoJson || REGIONS_DATA(this.region);
    },
    featureCollection() {
      const { data } = this.regionData;

      return feature(data, data.objects.subdivisions);
    },
  },
  methods: {
    findInData(item) {
      const names = pathOr('', ['properties', 'name'], item).split('|');
      const getAxis = prop(this.chartData.axis);

      const data = find((dataItem) => {
        const axis = getAxis(dataItem);

        return names.includes(axis);
      }, this.chartData.data);

      return data || {};
    },
  },
};
</script>
